import { PickupStore, UserAddress } from '@lentau/address/shared/interfaces';
import { DeliveryMode } from '@lentau/delivery-mode/shared/enums';
import { MissionAddressMode } from './types';

export class MissionAddressCacheMapper {
  // Мапы для сокращения названий полей в кеше (только используемые поля)
  private static readonly pickupStoreFieldMap: Record<string, string> = {
    id: 'i',
    alias: 'a',
    title: 't',
    addressFull: 'af',
    regionId: 'ri',
    marketType: 'mt',
    selected: 's',
  };

  private static readonly userAddressFieldMap: Record<string, string> = {
    Id: 'i',
    AddressFine: 'af',
    FullInfo: 'fi',
    StreetTitle: 'st',
    TownTitle: 'tt',
    Selected: 's',
    ZoneType: 'zt',
    FlatNumber: 'f',
    YandexLatitude: 'la',
    YandexLongitude: 'lo',
  };

  private static readonly missionAddressModeFieldMap: Record<string, string> = {
    type: 't',
    isDefaultStore: 'ids',
    missionAddress: 'ma',
  };

  private static readonly reverseMissionAddressModeFieldMap: Record<string, string> =
    MissionAddressCacheMapper.createReverseMap(MissionAddressCacheMapper.missionAddressModeFieldMap);

  private static createReverseMap(originalMap: Record<string, string>): Record<string, string> {
    return Object.entries(originalMap).reduce(
      (reverse: Record<string, string>, [key, value]: [string, string]) => {
        reverse[value] = key;
        return reverse;
      },
      {} as Record<string, string>
    );
  }

  private static isPickupStore(deliveryType: string): boolean {
    return deliveryType === DeliveryMode.Pickup || deliveryType === DeliveryMode.Shop;
  }

  // Собираем кеш из MissionAddressMode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static decomposeForCache(data: MissionAddressMode): Record<string, any> {
    if (!data) return null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const decomposed: any = {};

    // Используем сокращенные названия для основных полей
    if (data.type !== undefined) {
      decomposed[MissionAddressCacheMapper.missionAddressModeFieldMap.type] = data.type;
    }
    if (data.isDefaultStore !== undefined) {
      decomposed[MissionAddressCacheMapper.missionAddressModeFieldMap.isDefaultStore] = data.isDefaultStore;
    }

    if (data.missionAddress) {
      const missionAddressKey: string = MissionAddressCacheMapper.missionAddressModeFieldMap.missionAddress;

      const isPickupStore: boolean = MissionAddressCacheMapper.isPickupStore(data.type);

      if (isPickupStore) {
        // PickupStore
        decomposed[missionAddressKey] = {};
        const pickupStore: PickupStore = data.missionAddress as PickupStore;

        if (pickupStore.id !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.pickupStoreFieldMap.id] = pickupStore.id;
        }
        if (pickupStore.alias !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.pickupStoreFieldMap.alias] = pickupStore.alias;
        }
        if (pickupStore.title !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.pickupStoreFieldMap.title] = pickupStore.title;
        }
        if (pickupStore.addressFull !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.pickupStoreFieldMap.addressFull] =
            pickupStore.addressFull;
        }
        if (pickupStore.regionId !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.pickupStoreFieldMap.regionId] = pickupStore.regionId;
        }
        if (pickupStore.marketType !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.pickupStoreFieldMap.marketType] =
            pickupStore.marketType;
        }
        if (pickupStore.selected !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.pickupStoreFieldMap.selected] = pickupStore.selected;
        }
      } else {
        // UserAddress
        decomposed[missionAddressKey] = {};
        const userAddress: UserAddress = data.missionAddress as UserAddress;

        if (userAddress.Id !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.userAddressFieldMap.Id] = userAddress.Id;
        }
        if (userAddress.AddressFine !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.userAddressFieldMap.AddressFine] =
            userAddress.AddressFine;
        }
        if (userAddress.FullInfo !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.userAddressFieldMap.FullInfo] = userAddress.FullInfo;
        }
        if (userAddress.StreetTitle !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.userAddressFieldMap.StreetTitle] =
            userAddress.StreetTitle;
        }
        if (userAddress.TownTitle !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.userAddressFieldMap.TownTitle] =
            userAddress.TownTitle;
        }
        if (userAddress.Selected !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.userAddressFieldMap.Selected] = userAddress.Selected;
        }
        if (userAddress.ZoneType !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.userAddressFieldMap.ZoneType] = userAddress.ZoneType;
        }
        if (userAddress.FlatNumber !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.userAddressFieldMap.FlatNumber] =
            userAddress.FlatNumber;
        }
        if (userAddress.YandexLatitude !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.userAddressFieldMap.YandexLatitude] =
            userAddress.YandexLatitude;
        }
        if (userAddress.YandexLongitude !== undefined) {
          decomposed[missionAddressKey][MissionAddressCacheMapper.userAddressFieldMap.YandexLongitude] =
            userAddress.YandexLongitude;
        }
      }
    }

    return decomposed;
  }

  // Собираем объект MissionAddressMode из кеша
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static composeFromCache(decomposedData: Record<string, any>): MissionAddressMode {
    if (!decomposedData) return null;

    const composed: MissionAddressMode = {} as MissionAddressMode;

    const typeKey: string =
      MissionAddressCacheMapper.reverseMissionAddressModeFieldMap[
        MissionAddressCacheMapper.missionAddressModeFieldMap.type
      ];
    const isDefaultStoreKey: string =
      MissionAddressCacheMapper.reverseMissionAddressModeFieldMap[
        MissionAddressCacheMapper.missionAddressModeFieldMap.isDefaultStore
      ];
    const missionAddressKey: string =
      MissionAddressCacheMapper.reverseMissionAddressModeFieldMap[
        MissionAddressCacheMapper.missionAddressModeFieldMap.missionAddress
      ];

    if (decomposedData[MissionAddressCacheMapper.missionAddressModeFieldMap.type] !== undefined) {
      composed[typeKey] = decomposedData[MissionAddressCacheMapper.missionAddressModeFieldMap.type];
    }
    if (decomposedData[MissionAddressCacheMapper.missionAddressModeFieldMap.isDefaultStore] !== undefined) {
      composed[isDefaultStoreKey] = decomposedData[MissionAddressCacheMapper.missionAddressModeFieldMap.isDefaultStore];
    }

    if (decomposedData[MissionAddressCacheMapper.missionAddressModeFieldMap.missionAddress]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const missionAddressData: any =
        decomposedData[MissionAddressCacheMapper.missionAddressModeFieldMap.missionAddress];

      const isPickupStore: boolean = MissionAddressCacheMapper.isPickupStore(composed[typeKey]);

      if (isPickupStore) {
        // PickupStore
        composed[missionAddressKey] = {
          id: missionAddressData[MissionAddressCacheMapper.pickupStoreFieldMap.id],
          alias: missionAddressData[MissionAddressCacheMapper.pickupStoreFieldMap.alias],
          title: missionAddressData[MissionAddressCacheMapper.pickupStoreFieldMap.title],
          addressFull: missionAddressData[MissionAddressCacheMapper.pickupStoreFieldMap.addressFull],
          regionId: missionAddressData[MissionAddressCacheMapper.pickupStoreFieldMap.regionId],
          marketType: missionAddressData[MissionAddressCacheMapper.pickupStoreFieldMap.marketType],
          selected: missionAddressData[MissionAddressCacheMapper.pickupStoreFieldMap.selected],
          addressShort: missionAddressData[MissionAddressCacheMapper.pickupStoreFieldMap.addressFull] || '',
          // Добавляем значения по умолчанию для полей, которые не сохранялись
          coordinates: null,
          localTimeZone: '',
          localTimeZoneOffset: '',
          storeWorkingHours: null,
          pickupWindowHours: null,
          pickupEnable: true,
          isOffline: false,
          is24H: false,
          isZoo: false,
          isTobacco: false,
        } as PickupStore;
      } else {
        // UserAddress
        composed[missionAddressKey] = {
          Id: missionAddressData[MissionAddressCacheMapper.userAddressFieldMap.Id],
          AddressFine: missionAddressData[MissionAddressCacheMapper.userAddressFieldMap.AddressFine],
          FullInfo: missionAddressData[MissionAddressCacheMapper.userAddressFieldMap.FullInfo],
          StreetTitle: missionAddressData[MissionAddressCacheMapper.userAddressFieldMap.StreetTitle],
          TownTitle: missionAddressData[MissionAddressCacheMapper.userAddressFieldMap.TownTitle],
          Selected: missionAddressData[MissionAddressCacheMapper.userAddressFieldMap.Selected],
          ZoneType: missionAddressData[MissionAddressCacheMapper.userAddressFieldMap.ZoneType],
          FlatNumber: missionAddressData[MissionAddressCacheMapper.userAddressFieldMap.FlatNumber],
          YandexLatitude: missionAddressData[MissionAddressCacheMapper.userAddressFieldMap.YandexLatitude],
          YandexLongitude: missionAddressData[MissionAddressCacheMapper.userAddressFieldMap.YandexLongitude],
          // Добавляем значения по умолчанию для полей, которые не сохранялись
          DeliveryIntervalList: [],
          Editable: true,
          EntranceNumber: '',
          FlatFloor: '',
          HouseBuilding: '',
          HouseFrame: '',
          HouseNumber: '',
          Information: '',
          Intercom: '',
          IsVacationHome: false,
          isAddressMercury: false,
          isAddressEgais: false,
          isNewUser: false,
          b2biAddressId: '',
          Province: '',
          Area: '',
          B2BDeletable: false,
          District: '',
          Airport: '',
        } as UserAddress;
      }
    }

    return composed;
  }
}
